import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation, Link } from "react-router-dom";
import { Col, Card, CardBody, Spinner } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import AddToBookmarkCandidate from "../../helpers/Common/AddToBookmarkCandidate";
import BookmarkCandiRow from "../Account/Employer/BookMarkCandidates/BookmarkCandiRow";
import { getPlanDetails } from "../../actions/employer";
import { useDispatch } from "react-redux";
// =========================================================================
// SELF-CONTAINED LOCAL UTILITY HELPERS (Prevents undefined bundler errors)
// =========================================================================
const isInsufficientCreditError = (msg) => {
  return String(msg).toLowerCase().includes("credit");
};

const buyCreditsAlert = (message, pathname) => {
  Swal.fire({
    icon: "error",
    title: "Insufficient Credits",
    text: message || "You don't have enough credits to complete this action.",
    showCancelButton: true,
    confirmButtonText: "Buy Credits",
    confirmButtonColor: "#387ed8",
    cancelButtonColor: "#d33",
  }).then((result) => {
    if (result.isConfirmed) {
      window.location.href = "/buy-credits";
    }
  });
};

const apiErrorHandler = (error) => {
  console.error("API Call Intercepted Error:", error);
  Swal.fire({
    icon: "error",
    title: "Request Error",
    text:
      error?.response?.data?.message ||
      "Something went wrong. Please try again.",
  });
};

const toArray = (item, separator = ",") => {
  if (!item) return [];
  if (Array.isArray(item)) return item;
  return String(item)
    .split(separator)
    .map((s) => s.trim());
};

const humanReadableSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return "0 KB";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (e) {
    return dateString;
  }
};

const getJobLocationById = (address, locationsList) => {
  if (!locationsList || !locationsList.length || !address) return address || "";
  const found = locationsList.find((loc) => String(loc.id) === String(address));

  return found ? `${found.name}, ${found.state}` : address;
};

const userImage6 =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ccc'><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5-4-8-4z'/></svg>";

// ==========================================
// MAIN COMPONENT MODULE
// ==========================================
const LeftSideContent = ({ additional_fields, details }) => {
  const jobLocations = useSelector((state) => state.jobLocations.data) || [];
  const { pathname } = useLocation();

  const [loading, setLoading] = useState(false);
  const [candidateHiddenDetails, setCandidateHiddenDetails] = useState(null);

  // 🟢 FIXED: also treat the candidate as unlocked if they applied to a job
  // (isJobApplicant === true), on top of the existing alreadyViewed /
  // isContactLocked checks — matches the backend rule: "applied = free view".
  const determineInitialUnlockState = (payload) => {
    if (!payload) return false;
    if (payload.alreadyViewed === true) return true;
    if (payload.data?.alreadyViewed === true) return true;
    if (payload.data?.isContactLocked === false) return true;
    if (payload.data?.isJobApplicant === true) return true; // 🟢 NEW
    if (payload.isJobApplicant === true) return true; // 🟢 NEW (in case it's at the root)
    return false;
  };

  const [alreadyViewed, setAlreadyViewed] = useState(() =>
    determineInitialUnlockState(details),
  );

  useEffect(() => {
    if (details) {
      const isUnlocked = determineInitialUnlockState(details);
      setAlreadyViewed((currentLocalState) => {
        if (currentLocalState === true) return true;
        return isUnlocked;
      });
    }
  }, [details]);

  const candidateEmailAddress =
    candidateHiddenDetails?.data?.email || details?.data?.email;
  const candidateMobileNumber =
    candidateHiddenDetails?.data?.phoneNumber || details?.data?.phoneNumber;

  const fetchCustomerDetailsApi = async (customerId) => {
    const token = localStorage.getItem("webvi_authToken");
    const targetUrl = `https://sdsjobseekers.runasp.net/api/Candidate/${customerId}/employer-view`;

    try {
      const response = await axios.get(targetUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const responseData = response?.data;
      if (!responseData?.success) {
        throw new Error(responseData?.message || "Failed to retrieve details.");
      }
      return responseData;
    } catch (error) {
      console.error("API Request Failed:", error);
      const errorMessage = error?.response?.data?.message || error?.message;
      if (isInsufficientCreditError(errorMessage)) {
        buyCreditsAlert(errorMessage, pathname);
      } else {
        apiErrorHandler(error);
      }
      return null;
    }
  };

  const getCleanCandidateId = () => {
    let candidateId =
      details?.data?.userId ||
      details?.userId ||
      details?.data?.id ||
      details?.id;
    let source = "State Context (User/Account ID Mapping)";

    if (!candidateId || candidateId === 0 || candidateId === "0") {
      const urlSegments = window.location.pathname.split("/");
      const numericSegment = urlSegments.find(
        (segment) =>
          !isNaN(segment) && segment.trim() !== "" && segment !== "0",
      );
      if (numericSegment) {
        candidateId = Number(numericSegment);
        source = "URL route parameter fallback extraction";
      }
    }

    return candidateId;
  };
const dispatch = useDispatch();
const handleUnlockCandidateWorkflow = async (event) => {
  if (event) event.preventDefault();

  const candidateId = getCleanCandidateId();

  if (!candidateId) {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Could not resolve a valid Candidate ID.",
    });
    return false;
  }

  const result = await Swal.fire({
    title: "Unlock Candidate Details?",
    html: `<p>This will consume <b>1 Credit</b>.</p><p>Unlock full contact options, files, and social profiles.</p>`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Unlock Now",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#387ed8",
    cancelButtonColor: "#d33",
  });

  if (!result.isConfirmed) return false;

  try {
    setLoading(true);
    const payload = await fetchCustomerDetailsApi(candidateId);

    if (payload) {
      setCandidateHiddenDetails(payload);
      setAlreadyViewed(true);

      // ⚡ THIS IS THE FIX: Dispatch getPlanDetails to update Redux store!
      dispatch(getPlanDetails());

      Swal.fire({
        icon: "success",
        title: "Unlocked Successfully",
        timer: 1500,
        showConfirmButton: false,
      });
      return true;
    }
    return false;
  } catch (err) {
    console.error("❌ [Unlock] Unexpected error:", err);
    return false;
  } finally {
    setLoading(false);
  }
};

  const onSocialLinkClick = async (event, targetUrl) => {
    event.preventDefault();
    if (alreadyViewed) {
      window.open(targetUrl, "_blank");
      return;
    }

    const result = await Swal.fire({
      title: "Unlock Social Profile?",
      text: "1 credit will be deducted from your account to view this profile link.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#387ed8",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, unlock & view!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      const candidateId = getCleanCandidateId();
      setLoading(true);
      const payload = await fetchCustomerDetailsApi(candidateId);
      setLoading(false);

      if (payload) {
        setCandidateHiddenDetails(payload);
        setAlreadyViewed(true);
        window.open(targetUrl, "_blank");
      }
    }
  };

  // 🟢 NEW: shared helper — actually fetches the resume/cover-letter blob
  // and opens it in a new tab (used by both Preview and, indirectly, keeps
  // parity with the download flow's fetch logic).
  const openFileBlobInNewTab = async (candidateId) => {
    const token = localStorage.getItem("webvi_authToken");
    const targetUrl = `https://sdsjobseekers.runasp.net/api/Candidate/${candidateId}/download-resume`;

    const response = await axios.get(targetUrl, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });

    const fileBlob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/pdf",
    });
    const objectBlobUrl = window.URL.createObjectURL(fileBlob);

    // Naya tab open karo — browser jo bhi file type ho (PDF etc.) usko
    // preview kar dega, download nahi hoga
    window.open(objectBlobUrl, "_blank", "noopener,noreferrer");

    // Thodi der baad object URL revoke karo taaki naya tab load ho chuka ho
    setTimeout(() => window.URL.revokeObjectURL(objectBlobUrl), 60000);
  };

  // 🟢 NEW: Preview button handler — same unlock logic as confirmDownload,
  // just opens the file inline in a new tab instead of forcing a download.
 const confirmPreview = async () => {
  const candidateId = getCleanCandidateId();

  if (!alreadyViewed) {
    const result = await Swal.fire({
      title: "Unlock Resume?",
      text: "1 credit will be deducted to preview this candidate's resume.",
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#387ed8",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, preview!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    const payload = await fetchCustomerDetailsApi(candidateId);
    if (!payload) {
      setLoading(false);
      return;
    }
    setCandidateHiddenDetails(payload);
    setAlreadyViewed(true);

    // ⚡ UPDATE REDUX WALLET BALANCE
    dispatch(getPlanDetails());
  }

  try {
    setLoading(true);
    await openFileBlobInNewTab(candidateId);
  } catch (error) {
    console.error("File preview failed:", error);
    Swal.fire({
      icon: "error",
      title: "Preview Failed",
      text: "Unable to open document for preview.",
    });
  } finally {
    setLoading(false);
  }
};

const confirmDownload = async (type, originalFileName) => {
  const candidateId = getCleanCandidateId();

  if (!alreadyViewed) {
    const result = await Swal.fire({
      title: "Unlock Document?",
      text: "1 credit will be deducted to view and download this candidate's documents.",
      icon: "info",
      showCancelButton: true,
      confirmButtonColor: "#387ed8",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, download!",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    const payload = await fetchCustomerDetailsApi(candidateId);
    if (!payload) {
      setLoading(false);
      return;
    }
    setCandidateHiddenDetails(payload);
    setAlreadyViewed(true);

    // ⚡ UPDATE REDUX WALLET BALANCE
    dispatch(getPlanDetails());
  }

  try {
    setLoading(true);
    const token = localStorage.getItem("webvi_authToken");

    const targetUrl = `https://sdsjobseekers.runasp.net/api/Candidate/${candidateId}/download-resume`;

    const response = await axios.get(targetUrl, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });

    const extension = originalFileName
      ? originalFileName.split(".").pop()
      : "pdf";
    const cleanName = details?.data?.username
      ? details.data.username.replace(/\s+/g, "_")
      : `Candidate_${candidateId}`;
    let calculatedFileName = `${cleanName}_${type}.${extension}`;

    const fileBlob = new Blob([response.data], {
      type: response.headers["content-type"] || "application/octet-stream",
    });
    const objectBlobUrl = window.URL.createObjectURL(fileBlob);

    const anchorNode = document.createElement("a");
    anchorNode.href = objectBlobUrl;
    anchorNode.setAttribute("download", calculatedFileName);
    document.body.appendChild(anchorNode);
    anchorNode.click();

    document.body.removeChild(anchorNode);
    window.URL.revokeObjectURL(objectBlobUrl);
  } catch (error) {
    console.error("File download failed:", error);
    Swal.fire({
      icon: "error",
      title: "Download Failed",
      text: "Unable to compile and retrieve document from host stream.",
    });
  } finally {
    setLoading(false);
  }
};

  const getList = (item) => {
    return toArray(item, ",");
  };

  const SocialLinkButton = ({
    value,
    className = "btn-soft-primary",
    iconClass = "uil uil-facebook-f",
  }) => {
    if (!value) return null;
    const isWhatsAppLink = iconClass?.includes("whatsapp");
    const link = isWhatsAppLink ? `https://wa.me/+91${value}` : value;

    return (
      <li className="list-inline-item">
        <a
          href="#."
          onClick={(e) => onSocialLinkClick(e, link)}
          className={`social-link rounded-3 ${className}`}
          rel="noreferrer"
        >
          <i className={iconClass}></i>
        </a>
      </li>
    );
  };

  const maskEmail = (email) => {
    if (!email) return "********";
    const [name, domain] = email.split("@");
    return `${name.substring(0, 3)}*****@${domain}`;
  };

  const maskPhone = (phone) => {
    if (!phone) return "XXXXXXXXXX";
    return phone.replace(/\d(?=\d{3})/g, "X");
  };

  return (
    <React.Fragment>
      <Col lg={4}>
        <Card className="profile-sidebar">
          <CardBody className="p-4 position-relative">
            {loading && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75"
                style={{ zIndex: 10 }}
              >
                <Spinner color="primary" />
              </div>
            )}

            <div className="text-center pb-4 border-bottom">
              <div
                className="mx-auto d-flex align-items-center justify-content-center bg-white border rounded-4 shadow-sm overflow-hidden"
                style={{
                  width: "170px",
                  height: "170px",
                  borderRadius: "25px",
                }}
              >
                <img
                  src={(() => {
                    const fileName =
                      details?.data?.profileImage ||
                      details?.profileImage ||
                      details?.data?.photo;
                    if (
                      fileName &&
                      fileName !== "string" &&
                      fileName !== "null"
                    ) {
                      return `${fileName}`;
                    }
                    return userImage6;
                  })()}
                  alt="Profile"
                  className="img-fluid"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    padding: "10px",
                  }}
                />
              </div>

              <h5 className="mb-0 text-capitalize">{details?.data?.name}</h5>
              <p className="text-muted">{details?.data?.designation}</p>

              <ul className="candidate-detail-social-menu list-inline mb-0">
                <SocialLinkButton value={details?.data?.facebookUrl} />
                <SocialLinkButton
                  value={details?.data?.twitterUrl}
                  className="btn-soft-info"
                  iconClass="uil uil-instagram-alt"
                />
                <SocialLinkButton
                  value={details?.data?.whatsappNumber}
                  className="btn-soft-success"
                  iconClass="uil uil-whatsapp"
                />
                <SocialLinkButton
                  value={details?.data?.linkedinUrl}
                  className="btn-soft-primary"
                  iconClass="uil uil-linkedin-alt"
                />
              </ul>
            </div>

            <div className="mt-4 border-bottom pb-4">
              <h5 className="fs-17 fw-bold mb-3">Documents</h5>
              {!details?.data?.resume && !details?.data?.cover_letter && (
                <p style={{ fontSize: 13 }}>Documents are not uploaded yet.</p>
              )}

              <ul className="profile-document list-unstyled mb-0">
                {(details?.data?.resume || details?.resume) && (
                  <li>
                    <div className="profile-document-list d-flex align-items-center mt-4">
                      <div className="icon flex-shrink-0">
                        <i className="uil uil-file"></i>
                      </div>
                      <div className="ms-3">
                        <h6 className="fs-16 mb-0">
                          Resume.
                          {(details?.data?.resume || details?.resume)
                            ?.split(".")
                            .pop()}
                        </h6>
                        {(details?.data?.resume_size ||
                          details?.resume_size) && (
                          <p className="text-muted mb-0">
                            {humanReadableSize(
                              details?.data?.resume_size ||
                                details?.resume_size,
                            )}
                          </p>
                        )}
                      </div>
                      {/* 🟢 NEW: Preview button — same unlock logic as download,
                          opens resume inline in a new tab instead of downloading */}
                      <div className="ms-auto d-flex align-items-center gap-3">
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            confirmPreview();
                          }}
                          className="fs-20 text-muted"
                          title="Preview Resume"
                        >
                          <i className="uil uil-eye"></i>
                        </Link>
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            confirmDownload(
                              "resume",
                              details?.data?.resume || details?.resume,
                            );
                          }}
                          className="fs-20 text-muted"
                          title="Download Resume"
                        >
                          <i className="uil uil-import"></i>
                        </Link>
                      </div>
                    </div>
                  </li>
                )}

                {(details?.data?.cover_letter ||
                  additional_fields?.cover_letter) && (
                  <li>
                    <div className="profile-document-list d-flex align-items-center mt-4">
                      <div className="icon flex-shrink-0">
                        <i className="uil uil-file"></i>
                      </div>
                      <div className="ms-3">
                        <h6 className="fs-16 mb-0">
                          Cover-letter.
                          {(
                            details?.data?.cover_letter ||
                            additional_fields?.cover_letter
                          )
                            ?.split(".")
                            .pop()}
                        </h6>
                        {additional_fields?.cover_letter_size && (
                          <p className="text-muted mb-0">
                            {humanReadableSize(
                              additional_fields?.cover_letter_size,
                            )}
                          </p>
                        )}
                      </div>
                      <div className="ms-auto">
                        <Link
                          to="#"
                          onClick={(e) => {
                            e.preventDefault();
                            confirmDownload(
                              "cover_letter",
                              details?.data?.cover_letter ||
                                additional_fields?.cover_letter,
                            );
                          }}
                          className="fs-20 text-muted"
                        >
                          <i className="uil uil-import"></i>
                        </Link>
                      </div>
                    </div>
                  </li>
                )}
              </ul>
            </div>

            <div className="mt-4 border-bottom pb-4">
              <h5 className="fs-17 fw-bold mb-3">Personal Info</h5>
              <div className="profile-contact">
                <ul className="list-unstyled mb-0">
                  {details?.data?.gender && (
                    <li className="mt-3">
                      <h6 className="mb-0 me-2">Gender</h6>
                      <p className="text-muted mb-0">{details?.data?.gender}</p>
                    </li>
                  )}
                  {details?.data?.date_of_birth && (
                    <li className="mb-2">
                      <div className="d-flex align-items-center">
                        <h6 className="mb-0 me-2">Date of Birth:</h6>
                        <p className="text-muted mb-0">
                          {formatDate(details?.data?.date_of_birth)}
                        </p>
                      </div>
                    </li>
                  )}
                  {details?.data?.location && (
                    <li className="mt-3">
                      <h6>Location</h6>
                      <p className="mb-0">
                        {getJobLocationById(
                          details?.data?.location,
                          jobLocations,
                        )}
                      </p>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Contact Details Interface Info */}
            <div className="mt-4">
              <h5 className="fs-17 fw-bold mb-3">Contacts</h5>
              <div className="profile-contact">
                <ul className="list-unstyled mb-0">
                  <li className="mb-3">
                    <h6>Email</h6>
                    {alreadyViewed ? (
                      <a
                        href={`mailto:${candidateEmailAddress}`}
                        className="text-primary fw-medium"
                      >
                        {candidateEmailAddress}
                      </a>
                    ) : (
                      <span className="text-muted">
                        {maskEmail(candidateEmailAddress)}
                      </span>
                    )}
                  </li>

                  <li className="mb-3">
                    <h6>Phone</h6>
                    {alreadyViewed ? (
                      <a
                        href={`tel:${candidateMobileNumber}`}
                        className="text-primary fw-medium"
                      >
                        {candidateMobileNumber}
                      </a>
                    ) : (
                      <span className="text-muted">
                        {maskPhone(candidateMobileNumber)}
                      </span>
                    )}
                  </li>

                  {/* 🟢 The "Unlock" button now naturally disappears too, since alreadyViewed
                      is already true when isJobApplicant is true — no extra condition needed here */}
                  {!alreadyViewed && (
                    <li className="mt-3">
                      <button
                        className="btn btn-primary w-100 py-2"
                        onClick={handleUnlockCandidateWorkflow}
                      >
                        {loading
                          ? "Processing..."
                          : "🔓 Unlock Contact Details"}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>
            <AddToBookmarkCandidate
              candidateId={details?.data?.userId}
              showIcon={false}
              showText={true}
            />
          </CardBody>

          {(additional_fields?.skills ||
            additional_fields?.languages_known) && (
            <CardBody className="p-4 border-top">
              {additional_fields?.skills && (
                <div className="border-bottom pb-4 mb-4">
                  <h6 className="fs-17 fw-semibold mb-3">
                    Professional Skills
                  </h6>
                  <div className="d-flex flex-wrap align-items-start gap-1">
                    {getList(details?.data?.skills)?.map((item, index) => (
                      <span
                        key={"skill-" + index}
                        className="badge fs-13 bg-soft-success mt-2"
                        style={{
                          whiteSpace: "break-spaces",
                          lineHeight: "1.4",
                          textAlign: "left",
                        }}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {details?.data?.languages_known && (
                <div>
                  <h6 className="fs-17 fw-semibold mb-3">Spoken languages</h6>
                  <div className="d-flex flex-wrap align-items-start gap-1">
                    {getList(additional_fields?.languages_known)?.map(
                      (item, index) => (
                        <span
                          key={"lang-" + index}
                          className="badge fs-13 bg-soft-blue mt-2"
                        >
                          {item}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}
            </CardBody>
          )}
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default LeftSideContent;
